function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import clsx from 'clsx';
import clamp from 'lodash/clamp';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightReact } from 'common/components/text-highlight';
import type { Theme } from 'common/report/queries/app-api/themes';
import { getColorsForMode } from '../utils';
import styles from './index.module.styl';
export type SwatchOverflowMenuClickCallback = (params: {
  action: `edit` | `share` | `delete`;
  theme: Theme;
}) => void;

/**
 * Component for seeing a set of colors in a theme, and optionally clicking into colors
 */
export function ColorThemeSwatch(_ref: {
  theme: Theme;
  /** hides theme label row (name, tag, overflow menu) */
  hideName?: boolean;
  activeIndex?: number;
  onColorClick?: (index: number) => void;
  onColorDragDrop?: (dragIndex: number, dropIndex: number) => void;
  onSwatchClick?: () => void;
  isSelected?: boolean;
  tag?: string;
  overflowMenu?: JSX.Element;
  darkMode?: boolean;
}) {
  let {
    theme,
    hideName,
    activeIndex,
    onColorClick,
    onColorDragDrop,
    onSwatchClick,
    isSelected,
    tag,
    darkMode = undefined,
    overflowMenu
  }: {
    theme: Theme;
    /** hides theme label row (name, tag, overflow menu) */
    hideName?: boolean;
    activeIndex?: number;
    onColorClick?: (index: number) => void;
    onColorDragDrop?: (dragIndex: number, dropIndex: number) => void;
    onSwatchClick?: () => void;
    isSelected?: boolean;
    tag?: string;
    overflowMenu?: JSX.Element;
    darkMode?: boolean;
  } = _ref;
  const canClickColors = Boolean(onColorClick);
  const themeColors = getColorsForMode(theme, darkMode);
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  const [dragStartX, setDragStartX] = React.useState<number | null>(null);
  const [dragDeltaX, setDragDeltaX] = React.useState<number | null>(null);
  const [dropIndex, setDropIndex] = React.useState<number | null>(null);
  const startDrag = (event: React.PointerEvent, colorIndex: number) => {
    if (!onColorDragDrop || event.button !== 0) {
      // presence of onColorDragDrop callback enables drag and drop. Only allow left click
      return;
    }
    setDragIndex(colorIndex);
    setDragStartX(event.clientX);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const drag = (event: React.PointerEvent) => {
    if (!onColorDragDrop || dragIndex === null) {
      return;
    }
    const width = event.currentTarget.getBoundingClientRect().width;
    const parentRect = (event.currentTarget.parentNode as Element).getBoundingClientRect();
    const minDeltaX = parentRect.left - dragStartX - width / 4;
    const maxDeltaX = parentRect.right - dragStartX + width / 4;
    const deltaX = event.clientX - dragStartX;
    setDragDeltaX(clamp(deltaX, minDeltaX, maxDeltaX));
    const hoveredIndex = Math.trunc(deltaX / width) + dragIndex;
    const newDropIndex = clamp(hoveredIndex, 0, themeColors.length - 1);
    if (newDropIndex !== dropIndex) {
      setDropIndex(newDropIndex === dragIndex ? null : newDropIndex);
    }
  };
  const endDrag = () => {
    if (!onColorDragDrop) {
      return;
    }
    if (dropIndex !== null) {
      onColorDragDrop(dragIndex, dropIndex);
    }
    setDragIndex(null);
    setDragStartX(null);
    setDragDeltaX(null);
    setDropIndex(null);
  };
  return <div className={clsx(styles.swatch, {
    [styles.clickable]: onSwatchClick,
    [styles.selected]: isSelected
  })} data-sentry-component="ColorThemeSwatch" data-sentry-source-file="index.tsx">
      {!hideName && <div className={styles.label}>
          {theme.name}
          {tag ? <TextHighlightReact className={styles.tag} text={tag} size={SIZE.SMALL} theme={TEXT_HIGHLIGHT_THEME.GENERIC} /> : null}
          {overflowMenu}
        </div>}
      <div elref='themeSwatch' className={styles.swatchColors} onClick={() => onSwatchClick && onSwatchClick()} style={{
      '--drop-index': dropIndex
    }}>
        {themeColors.map((color, colorIndex) => {
        const isBeingDragged = dragIndex === colorIndex && dragDeltaX;
        const dragTransform = isBeingDragged ? {
          transform: "translateX(".concat(dragDeltaX, "px)")
        } : {};
        return <ButtonReact elref='themeSwatchColor' key={"".concat(theme.id).concat(colorIndex).concat(color)} theme={ButtonTheme.Tertiary} disabled={!canClickColors} readonly={!canClickColors} className={clsx(styles.item, {
          [styles.clickable]: canClickColors,
          [styles.selected]: colorIndex === activeIndex,
          [styles.dragging]: isBeingDragged,
          [styles.dropDivider]: dropIndex === colorIndex,
          [styles.dividerRight]: dropIndex === colorIndex && dragIndex < colorIndex
        })} disablePressedStyles style={_objectSpread({
          backgroundColor: color
        }, dragTransform)}
        // onClick is needed in addition to onPointerDown since "enter" key simulates a click
        onClick={() => canClickColors && (onColorClick === null || onColorClick === void 0 ? void 0 : onColorClick(colorIndex))} onPointerDown={ev => startDrag(ev, colorIndex)} onPointerMove={drag} onPointerUp={endDrag} onPointerCancel={endDrag} onLostPointerCapture={endDrag} />;
      })}
      </div>
    </div>;
}